import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users, Loader2, Eye, EyeOff, ExternalLink, Power, Search } from "lucide-react";
import { api, apiError, rupiah } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { KtpPreview } from "@/components/KtpPreview";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const FIELDS = [
  ["nama_panggilan", "Nama Singkat / Panggilan (ID)"],
  ["nama_lengkap", "Nama Lengkap"],
  ["no_ktp", "No KTP"],
  ["link_ktp", "Link Foto KTP"],
  ["email", "Email"],
  ["no_hp", "No HP"],
  ["no_rek_pribadi", "No Rek Pribadi"],
  ["sekuritas", "Sekuritas"],
  ["kode_nasabah", "Kode Nasabah"],
  ["no_sid", "No SID"],
  ["username", "Username Login"],
  ["pass_sekuritas", "Pass Sekuritas"],
  ["no_rek_rdn", "No Rek RDN"],
  ["sub_rekening_efek", "Sub Rekening Efek"],
  ["password", "Password (di-generate Admin)"],
  ["pin_trading", "PIN Trading"],
  ["downline", "Downline (jumlah)"],
];

const empty = { ...Object.fromEntries(FIELDS.map(([k]) => [k, ""])), keterangan: "", downline: 0 };

export default function AnggotaPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [cari, setCari] = useState("");
  const [filterStatus, setFilterStatus] = useState("semua");
  const [ktpPreview, setKtpPreview] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/anggota");
      setRows(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!form.nama_panggilan.trim()) return toast.error("Nama panggilan wajib diisi");
    setSaving(true);
    try {
      const payload = { ...empty, ...form, downline: Number(form.downline) || 0 };
      delete payload.id;
      if (editing) await api.put(`/anggota/${editing.id}`, payload);
      else await api.post("/anggota", payload);
      toast.success(editing ? "Data anggota diperbarui" : "Anggota baru ditambahkan");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row) => {
    const next = !(row.aktif !== false);
    try {
      await api.patch(`/anggota/${row.id}/status`, { aktif: next });
      toast.success(`Anggota ${row.nama_panggilan} kini ${next ? "AKTIF" : "NONAKTIF"}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Hapus anggota ${row.nama_panggilan} beserta mutasinya?`)) return;
    try {
      await api.delete(`/anggota/${row.id}`);
      toast.success("Anggota dihapus");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const kunci = cari.trim().toLowerCase();
  const daftar = rows.filter((r) => {
    const cocokTeks =
      !kunci ||
      (r.nama_panggilan || "").toLowerCase().includes(kunci) ||
      (r.nama_lengkap || "").toLowerCase().includes(kunci);
    const aktif = r.aktif !== false;
    const cocokStatus =
      filterStatus === "semua" ||
      (filterStatus === "aktif" && aktif) ||
      (filterStatus === "nonaktif" && !aktif);
    return cocokTeks && cocokStatus;
  });

  return (
    <div data-testid="anggota-page">
      <PageHeader
        eyebrow="Data Anggota"
        title="Master Data Anggota"
        description="Nama panggilan berfungsi sebagai kunci relasi ke akun login, saldo, dan mutasi anggota."
      >
        <Button
          data-testid="anggota-add-btn"
          onClick={() => {
            setEditing(null);
            setForm(empty);
            setOpen(true);
          }}
          className="rounded-full bg-[#00d09c] font-semibold text-[#080b11] hover:bg-[#00e5ff]"
        >
          <Plus size={16} className="mr-1.5" /> Tambah Anggota
        </Button>
      </PageHeader>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative sm:w-72">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4d586f]" />
          <Input
            data-testid="anggota-search-input"
            value={cari}
            onChange={(e) => setCari(e.target.value)}
            placeholder="Cari nama panggilan atau nama lengkap"
            className="h-9 border-[#1e2536] bg-[#10141d] pl-9 text-sm text-white placeholder:text-[#3a4457] focus-visible:ring-[#00d09c]"
          />
        </div>
        <div className="flex gap-1.5">
          {[
            ["semua", "Semua"],
            ["aktif", "Aktif"],
            ["nonaktif", "Nonaktif"],
          ].map(([nilai, label]) => (
            <Button
              key={nilai}
              data-testid={`anggota-filter-${nilai}`}
              variant="outline"
              onClick={() => setFilterStatus(nilai)}
              className={`h-9 border-[#1e2536] text-xs transition-colors duration-200 ${
                filterStatus === nilai
                  ? "bg-[#00d09c]/10 text-[#00d09c]"
                  : "bg-transparent text-[#8f9bb3] hover:text-white"
              }`}
            >
              {label}
            </Button>
          ))}
        </div>
        <Button
          data-testid="anggota-toggle-secret-btn"
          variant="outline"
          onClick={() => setShowSecret((s) => !s)}
          className="h-9 border-[#1e2536] bg-transparent text-xs text-[#8f9bb3] hover:text-white sm:ml-auto"
        >
          {showSecret ? <EyeOff size={14} className="mr-1.5" /> : <Eye size={14} className="mr-1.5" />}
          {showSecret ? "Sembunyikan" : "Tampilkan"} data sensitif
        </Button>
      </div>

      <p data-testid="anggota-count" className="font-mono-num mb-3 text-xs text-[#4d586f]">
        Menampilkan {daftar.length} dari {rows.length} anggota
      </p>

      <div className="overflow-hidden rounded-lg border border-[#1e2536] bg-[#10141d]">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="animate-spin text-[#2b3550]" />
          </div>
        ) : daftar.length === 0 ? (
          <EmptyState
            icon={Users}
            title={rows.length === 0 ? "Belum ada anggota" : "Tidak ada anggota yang cocok"}
            hint={
              rows.length === 0
                ? "Tambahkan anggota untuk mulai mencatat saldo."
                : "Coba ubah kata pencarian atau filter status."
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="anggota-table" className="w-full min-w-[2160px] text-sm">
              <thead>
                <tr className="border-b border-[#1e2536] bg-[#0d1119] text-left text-xs uppercase tracking-wider text-[#4d586f]">
                  <th className="sticky left-0 z-20 w-[130px] min-w-[130px] bg-[#0d1119] px-4 py-3 font-medium">Panggilan</th>
                  <th className="sticky left-[130px] z-20 w-[190px] min-w-[190px] border-r border-[#1e2536] bg-[#0d1119] px-4 py-3 font-medium">Aksi</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Nama Lengkap</th>
                  <th className="px-4 py-3 font-medium">No KTP</th>
                  <th className="px-4 py-3 font-medium">Link Foto KTP</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">No HP</th>
                  <th className="px-4 py-3 font-medium">Rek Pribadi</th>
                  <th className="px-4 py-3 font-medium">Sekuritas</th>
                  <th className="px-4 py-3 font-medium">Kode Nasabah</th>
                  <th className="px-4 py-3 font-medium">No SID</th>
                  <th className="px-4 py-3 font-medium">Username</th>
                  <th className="px-4 py-3 font-medium">Pass Sekuritas</th>
                  <th className="px-4 py-3 font-medium">Rek RDN</th>
                  <th className="px-4 py-3 font-medium">Sub Rek Efek</th>
                  <th className="px-4 py-3 font-medium">Password</th>
                  <th className="px-4 py-3 font-medium">PIN</th>
                  <th className="px-4 py-3 font-medium">Downline</th>
                  <th className="px-4 py-3 font-medium">Saldo</th>
                  <th className="px-4 py-3 font-medium">Keterangan</th>
                </tr>
              </thead>
              <tbody>
                {daftar.map((r) => (
                  <tr key={r.id} data-testid={`anggota-row-${r.nama_panggilan}`} className="border-b border-[#1a2030] last:border-0 hover:bg-white/[0.03]">
                    <td className="sticky left-0 z-20 w-[130px] min-w-[130px] whitespace-nowrap bg-[#10141d] px-4 py-3 font-semibold text-[#00d09c]">
                      {r.nama_panggilan}
                    </td>
                    <td className="sticky left-[130px] z-20 w-[190px] min-w-[190px] border-r border-[#1e2536] bg-[#10141d] px-4 py-3">
                      <div className="flex gap-2">
                        <Button
                          data-testid={`anggota-toggle-status-btn-${r.nama_panggilan}`}
                          size="icon"
                          variant="outline"
                          title={r.aktif !== false ? "Nonaktifkan anggota" : "Aktifkan anggota"}
                          onClick={() => toggleStatus(r)}
                          className={`h-8 w-8 border-[#1e2536] bg-transparent ${
                            r.aktif !== false
                              ? "text-[#00d09c] hover:text-[#ff3b30]"
                              : "text-[#ff3b30] hover:text-[#00d09c]"
                          }`}
                        >
                          <Power size={14} />
                        </Button>
                        <Link to={`/dashboard/${encodeURIComponent(r.nama_panggilan)}`}>
                          <Button
                            data-testid={`anggota-view-btn-${r.nama_panggilan}`}
                            size="icon"
                            variant="outline"
                            className="h-8 w-8 border-[#1e2536] bg-transparent text-[#8f9bb3] hover:text-[#00e5ff]"
                          >
                            <ExternalLink size={14} />
                          </Button>
                        </Link>
                        <Button
                          data-testid={`anggota-edit-btn-${r.nama_panggilan}`}
                          size="icon"
                          variant="outline"
                          onClick={() => {
                            setEditing(r);
                            setForm({ ...empty, ...r });
                            setOpen(true);
                          }}
                          className="h-8 w-8 border-[#1e2536] bg-transparent text-[#8f9bb3] hover:text-[#00d09c]"
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          data-testid={`anggota-delete-btn-${r.nama_panggilan}`}
                          size="icon"
                          variant="outline"
                          onClick={() => remove(r)}
                          className="h-8 w-8 border-[#1e2536] bg-transparent text-[#8f9bb3] hover:text-[#ff3b30]"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        data-testid={`anggota-status-${r.nama_panggilan}`}
                        className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs uppercase tracking-wider ${
                          r.aktif !== false
                            ? "bg-[#00d09c]/10 text-[#00d09c]"
                            : "bg-[#ff3b30]/10 text-[#ff3b30]"
                        }`}
                      >
                        {r.aktif !== false ? "Aktif" : "Nonaktif"}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-white">{r.nama_lengkap || "-"}</td>
                    <td className="font-mono-num px-4 py-3 text-[#8f9bb3]">{showSecret ? r.no_ktp || "-" : "••••••"}</td>
                    <td className="px-4 py-3">
                      {r.link_ktp ? (
                        <button
                          data-testid={`anggota-link-ktp-${r.nama_panggilan}`}
                          onClick={() => setKtpPreview(r)}
                          className="text-[#00d09c] underline-offset-2 transition-colors duration-200 hover:text-[#00e5ff] hover:underline"
                        >
                          Lihat foto
                        </button>
                      ) : (
                        <span className="text-[#4d586f]">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#8f9bb3]">{r.email || "-"}</td>
                    <td className="font-mono-num px-4 py-3 text-[#8f9bb3]">{r.no_hp || "-"}</td>
                    <td className="font-mono-num px-4 py-3 text-[#8f9bb3]">{showSecret ? r.no_rek_pribadi || "-" : "••••••"}</td>
                    <td className="px-4 py-3 text-[#8f9bb3]">{r.sekuritas || "-"}</td>
                    <td className="font-mono-num px-4 py-3 text-[#8f9bb3]">{r.kode_nasabah || "-"}</td>
                    <td className="font-mono-num px-4 py-3 text-[#8f9bb3]">{r.no_sid || "-"}</td>
                    <td className="px-4 py-3 text-[#8f9bb3]">{r.username || "-"}</td>
                    <td className="font-mono-num px-4 py-3 text-[#8f9bb3]">{showSecret ? r.pass_sekuritas || "-" : "••••••"}</td>
                    <td className="font-mono-num px-4 py-3 text-[#8f9bb3]">{showSecret ? r.no_rek_rdn || "-" : "••••••"}</td>
                    <td className="font-mono-num px-4 py-3 text-[#8f9bb3]">{showSecret ? r.sub_rekening_efek || "-" : "••••••"}</td>
                    <td className="font-mono-num px-4 py-3 text-[#8f9bb3]">{showSecret ? r.password || "-" : "••••••"}</td>
                    <td className="font-mono-num px-4 py-3 text-[#8f9bb3]">{showSecret ? r.pin_trading || "-" : "••••"}</td>
                    <td className="font-mono-num px-4 py-3">
                      <span className={Number(r.downline) > 0 ? "text-[#00d09c]" : "text-[#8f9bb3]"}>
                        {Number(r.downline) || 0}
                      </span>
                    </td>
                    <td className="font-mono-num whitespace-nowrap px-4 py-3 font-semibold text-white">{rupiah(r.saldo)}</td>
                    <td className="max-w-[220px] px-4 py-3 text-[#8f9bb3]">{r.keterangan || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <KtpPreview
        nama={ktpPreview?.nama_panggilan}
        link={ktpPreview?.link_ktp}
        open={!!ktpPreview}
        onOpenChange={(v) => !v && setKtpPreview(null)}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="anggota-dialog" className="max-h-[88vh] overflow-y-auto border-[#1e2536] bg-[#10141d] sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editing ? `Edit Anggota ${editing.nama_panggilan}` : "Tambah Anggota Baru"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            {FIELDS.map(([key, label]) => (
              <div key={key}>
                <Label className="text-xs uppercase tracking-[0.12em] text-[#8f9bb3]">{label}</Label>
                <Input
                  data-testid={`anggota-input-${key}`}
                  value={form[key] || ""}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="mt-1.5 border-[#1e2536] bg-[#0d1119] text-white focus-visible:ring-[#00d09c]"
                />
              </div>
            ))}
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-[0.12em] text-[#8f9bb3]">Keterangan</Label>
              <Textarea
                data-testid="anggota-input-keterangan"
                value={form.keterangan || ""}
                onChange={(e) => setForm({ ...form, keterangan: e.target.value })}
                className="mt-1.5 border-[#1e2536] bg-[#0d1119] text-white focus-visible:ring-[#00d09c]"
              />
            </div>
          </div>
          <p className="text-xs text-[#4d586f]">
            Mengisi Username + Password akan otomatis membuat akun login role Anggota yang terhubung
            ke nama panggilan ini.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-[#1e2536] bg-transparent text-[#8f9bb3]">
              Batal
            </Button>
            <Button
              data-testid="anggota-save-btn"
              onClick={save}
              disabled={saving}
              className="rounded-full bg-[#00d09c] font-semibold text-[#080b11] hover:bg-[#00e5ff]"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
